import { Component, inject, OnInit, signal } from "@angular/core";
import { FormsModule, ReactiveFormsModule, Validators } from "@angular/forms";
import { BasicEditForm } from "@shared/form/basic/basic-edit.form";
import { IFormFields } from "@shared/form/basic/basic-form.interface";
import { CompaniesService } from "../../../services/companies.service";
import { RegionService } from "@modules/geography/services/region.service";
import { OwnerShipTypesService } from "@modules/spravochnik/services/owner-ship-types.service";
import { DistrictService } from "@modules/geography/services/district.service";
import { CabButtonComponent } from "@shared/form/components/cab-button/cab-button.component";
import { CabTextareaComponent } from "@shared/form/components/cab-textarea/cab-textarea.component";
import { CabSelectComponent } from "@shared/form/components/cab-select/cab-select.component";
import { CabAutocompleteComponent } from "@shared/form/components/cab-autocomplete/cab-autocomplete.component";
import { CabInputComponent } from "@shared/form/components/cab-input/cab-input.component";
import { CabFileInputComponent } from "@shared/form/components/cab-file-input/cab-file-input.component";
import { CabLoadingComponent } from "@shared/cab-loading/cab-loading.component";
import { CabCalendarComponent } from "@shared/form/components/cab-calendar/cab-calendar.component";
import { EBucketNames } from "@core/enums/EBucketNames.enum";
import { LocalService } from "@core/services/local-storage.service";
import { ELocalStorage } from "@core/enums/LocalStorage.enum";
import { ERoless } from "@core/enums/Roles.enum";
import { patternValidator } from "@shared/form/components/cab-validators/patter.validator";
import { CabInputPercentComponent } from "@shared/form/components/cab-input-percent/cab-input-percent.component";

@Component({
  selector: "app-companies-modal-edit",
  templateUrl: "../companies-modal-form.component.html",
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    CabInputComponent,
    CabAutocompleteComponent,
    CabSelectComponent,
    CabTextareaComponent,
    CabButtonComponent,
    CabFileInputComponent,
    CabLoadingComponent,
    CabCalendarComponent,
    CabInputPercentComponent,
  ],
})
export class CompaniesModalEditComponent
  extends BasicEditForm
  implements OnInit
{
  _defaultService = inject(CompaniesService);
  _regionService = inject(RegionService);
  _ownerShipTypeService = inject(OwnerShipTypesService);
  _districtService = inject(DistrictService);
  _localStorageService = inject(LocalService);

  isEdit = signal(true);

  statusAction = [
    { name: "TOOLS.ORG_EMPLOYEES.STATUS.ACTIVE", id: true },
    { name: "TOOLS.ORG_EMPLOYEES.STATUS.INACTIVE", id: false },
  ];

  minDate: Date;

  ngOnInit() {
    super.ngOnInit();
    this.form.get("contractStartDate")?.valueChanges.subscribe((val) => {
      this.minDate = new Date(val);
    });
  }

  formFields: IFormFields = {
    id: this.createFormField(0),
    name: this.createFormField(null, [
      Validators.required,
      Validators.maxLength(255),
    ]),
    ownershipTypeId: this.createFormField(null, [Validators.required]),
    regionId: this.createFormField(null, [Validators.required]),
    districtId: this.createFormField(null, [Validators.required]),
    directorFio: this.createFormField(null, [Validators.required]),
    isActive: this.createFormField(null, [Validators.required]),
    transitAccMfo: this.createFormField(null, [Validators.required]),
    bankName: this.createFormField(null, [
      Validators.required,
      Validators.maxLength(120),
      Validators.minLength(5),
    ]),
    bankMfo: this.createFormField(null, [
      Validators.required,
      Validators.maxLength(20),
    ]),
    inn: this.createFormField(null, [
      Validators.required,
      Validators.maxLength(15),
    ]),
    transitAccountNumber: this.createFormField(null, [
      Validators.required,
      Validators.minLength(20),
      Validators.maxLength(20),
    ]),
    legalAccountNumber: this.createFormField(null),
    companyAccountNumber: this.createFormField(null, [
      Validators.required,
      Validators.minLength(20),
      Validators.maxLength(20),
    ]),
    email: this.createFormField(null),
    address: this.createFormField(null, [
      Validators.required,
      Validators.maxLength(255),
      Validators.minLength(5),
    ]),
    phone: this.createFormField(null),
    dxshPercentage: this.createFormField(null),
    description: this.createFormField(null, [Validators.maxLength(200)]),
    smsName: this.createFormField(null, [
      Validators.required,
      Validators.maxLength(19),
      Validators.minLength(3),
      patternValidator({ regex: /^[A-Za-z \s/-]*$/, infoText: "A~Z, -, a-z." }),
    ]),
  };

  override beforeSubmit(): void {
    this.isPending.set(true);

    const phone =
      "+" + (this.form.get("phone")?.value || "").replace(/\D/g, "");
    this.form.get("phone")?.patchValue(phone);
  }

  hasPermission() {
    const userRole = this._localStorageService.getData(ELocalStorage.ROLE);
    return [ERoless.SUPER_ADMIN, ERoless.REPUBLIC_ADMIN].includes(userRole);
  }

  protected readonly EBucketNames = EBucketNames;
}
